/**
 * Network connectivity service.
 * Uses @capacitor/network on native platforms, falls back to navigator.onLine on web.
 */
import { Capacitor } from '@capacitor/core';
import { Network, ConnectionStatus } from '@capacitor/network';

type NetworkChangeCallback = (isConnected: boolean) => void;

class NetworkService {
  private listeners = new Set<NetworkChangeCallback>();
  private currentStatus: boolean = navigator.onLine;
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    if (Capacitor.isNativePlatform()) {
      // Use Capacitor Network plugin on native
      const status = await Network.getStatus();
      this.currentStatus = status.connected;

      Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
        this.currentStatus = status.connected;
        this.notifyListeners(status.connected);
      });
    } else {
      // Web fallback
      this.currentStatus = navigator.onLine;
      window.addEventListener('online', () => {
        this.currentStatus = true;
        this.notifyListeners(true);
      });
      window.addEventListener('offline', () => {
        this.currentStatus = false;
        this.notifyListeners(false);
      });
    }
  }

  isOnline(): boolean {
    return this.currentStatus;
  }

  addListener(callback: NetworkChangeCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(isConnected: boolean): void {
    this.listeners.forEach(cb => cb(isConnected));
  }
}

export const networkService = new NetworkService();
